import { api } from '@/utils/api';
import { AuthResponse, LoginCredentials, RegisterData, User } from '@/types';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
class AuthService {
  private readonly BASE_URL = '/auth';

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // api.post() already returns response.data = { success, message, data }
      const response = await api.post<any>(`${this.BASE_URL}/login`, credentials);
      return response.data || response;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || 'Login failed');
    }
  }

  /**
   * Register new organization and admin user
   */
  async register(userData: RegisterData): Promise<AuthResponse> {
    try {
      const slugBase = (userData.organizationName || 'workspace')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 40) || 'workspace';

      const response = await api.post<any>('/tenants/register', {
        organizationName: userData.organizationName,
        slug: `${slugBase}-${Date.now().toString().slice(-4)}`,
        adminEmail: userData.email,
        adminPassword: userData.password,
        adminFirstName: userData.firstName,
        adminLastName: userData.lastName,
        plan: 'SOLO',
      });

      const payload = response?.data || response;
      const authData = payload?.data || payload;

      if (!authData?.user || !authData?.tokens) {
        throw new Error('Invalid registration response');
      }

      return {
        user: {
          ...authData.user,
          tenant: authData.tenant,
        },
        tokens: authData.tokens,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || 'Registration failed');
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post(`${this.BASE_URL}/logout`);
    } catch (error: any) {
      console.error('Logout error:', error);
      // Don't throw - allow local logout even if API call fails
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    try {
      const response = await api.post<any>(`${this.BASE_URL}/refresh`, {
        refreshToken,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Token refresh failed');
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    try {
      // api.get() already returns response.data = { success, message, data }
      const response = await api.get<any>(`${this.BASE_URL}/me`);
      return response.data || response;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to get user');
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    try {
      const response = await api.put<any>(`${this.BASE_URL}/profile`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to update profile');
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await api.post(`${this.BASE_URL}/change-password`, {
        currentPassword,
        newPassword,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Failed to change password');
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<void> {
    try {
      await api.post(`${this.BASE_URL}/forgot-password`, { email });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Failed to request password reset');
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await api.post(`${this.BASE_URL}/reset-password`, {
        token,
        newPassword,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Failed to reset password. The link may have expired.');
    }
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    try {
      await api.post(`${this.BASE_URL}/verify-email`, { token });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Email verification failed');
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
